import { useEffect, useState } from 'react';

import {
  Check,
  Close,
  Delete,
  Edit,
  Search,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';

import {
  Bookmark,
  selectDisplayedBookmark,
  selectInLocationEditMode,
  setDisplayedBookmark,
  setInLocationEditMode,
  useBookmarkCreateMutation,
  useBookmarkDeleteMutation,
  useBookmarkUpdateMutation,
  useGetBookmarksQuery,
} from 'state/bookmarkSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';

import { SOMETHING_WENT_WRONG_SAVING_MSG, getSavedSuccessMsg } from 'appUtils';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { showToast } from 'features/common/toasts/ToastProvider';
import { updateNameError } from 'features/common/utils';

import {
  selectMapZoom,
  selectProject,
  selectWorkflow,
  setMapCenter,
  setMapZoom,
} from '../../state/workflowSlice';
import { InfoWalkmeCrumb } from '../info_hover/InfoHover';
import {
  jsonPointToLatLng,
  latLngToJsonPoint,
} from '../map/utils/geography_utils';

import styles from './BookmarkToolbar.module.css';

export function BookmarkToolbar({ showTitle = true }: { showTitle?: boolean }) {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [editBookmarkName, setEditBookmarkName] = useState('');
  const [editBookmarkId, setEditBookmarkId] = useState(undefined);
  const [deleteBookmarkId, setDeleteBookmarkId] = useState(undefined);
  const project_slug = useAppSelector(selectProject);
  const workflow = useAppSelector(selectWorkflow);
  const displayedBookmark = useAppSelector(selectDisplayedBookmark);
  const inLocationEditMode = useAppSelector(selectInLocationEditMode);
  const mapZoom = useAppSelector(selectMapZoom);

  const { currentData: bookmarkData } = useGetBookmarksQuery(project_slug, {
    skip: !project_slug,
  });
  const [updateBookmark] = useBookmarkUpdateMutation();
  const [createBookmark] = useBookmarkCreateMutation();
  const [deleteBookmark] = useBookmarkDeleteMutation();

  let bookmarks = bookmarkData?.bookmarks ? [...bookmarkData.bookmarks] : [];
  bookmarks = bookmarks.sort((a, b) => +b.starred - +a.starred);
  if (search && search !== '' && showSearch) {
    bookmarks = bookmarks.filter((value) =>
      value.name.toLowerCase().includes(search.toLowerCase()),
    );
  }

  function exitEditMode() {
    dispatch(setInLocationEditMode(false));
    setEditBookmarkId(undefined);
    setEditBookmarkName('');
    dispatch(setDisplayedBookmark(undefined));
  }

  useEffect(() => {
    // When we switch project or workflow, we should exit edit mode because its otherwise confusing
    exitEditMode();
  }, [project_slug, workflow]);

  const onBookmarkSelect = (_e, index) => {
    exitEditMode();
    dispatch(setMapCenter(jsonPointToLatLng(bookmarks[index].center)));
    if (bookmarks[index].zoom) {
      dispatch(setMapZoom(bookmarks[index].zoom));
    }
  };

  const onBookmarkStarClick = (_e, index) => {
    const newBookmark = { ...bookmarks[index] };
    newBookmark.starred = !newBookmark.starred;
    updateBookmark({
      project_slug: project_slug as string,
      bookmark_id: newBookmark.id,
      bookmark: newBookmark,
    });
  };

  const onBookmarkEditClick = (_e, index) => {
    const bookmark = bookmarks[index];
    dispatch(setDisplayedBookmark(jsonPointToLatLng(bookmark.center)));
    dispatch(setMapCenter(jsonPointToLatLng(bookmark.center)));
    if (bookmark.zoom) {
      dispatch(setMapZoom(bookmark.zoom));
    }
    dispatch(setInLocationEditMode(true));
    setEditBookmarkName(bookmark.name);
    setEditBookmarkId(bookmark.id);
  };

  const onBookmarkSubmitClick = (_e, index) => {
    const bookmark = bookmarks[index];
    console.assert(bookmark.id === editBookmarkId);
    updateBookmark({
      project_slug: project_slug as string,
      bookmark_id: editBookmarkId,
      bookmark: {
        id: editBookmarkId,
        name: editBookmarkName,
        center: latLngToJsonPoint(displayedBookmark),
        zoom: mapZoom,
        starred: true,
      },
    });
    exitEditMode();
  };

  const onAddSubmit = (_e) => {
    dispatch(setInLocationEditMode(false));
    createBookmark({
      project_slug: project_slug as string,
      bookmark_id: editBookmarkId,
      bookmark: {
        id: editBookmarkId,
        name: editBookmarkName,
        center: latLngToJsonPoint(displayedBookmark),
        zoom: mapZoom,
        starred: true,
      },
    })
      .unwrap()
      .then(() => showToast('success', getSavedSuccessMsg('location')))
      .catch((e) => {
        showToast('error', SOMETHING_WENT_WRONG_SAVING_MSG);
        console.error(e);
      });
    exitEditMode();
  };

  const onBookmarkDeleteConfirmClick = (_e, index) => {
    const bookmark = bookmarks[index];
    console.assert(
      bookmark.id === deleteBookmarkId,
      deleteBookmarkId,
      bookmark.id,
      bookmark,
    );
    deleteBookmark({
      project_slug: project_slug as string,
      bookmark_id: deleteBookmarkId,
    });
    setDeleteBookmarkId(undefined);
  };

  const onAddActivate = (_e) => {
    dispatch(setDisplayedBookmark(undefined));
    dispatch(setInLocationEditMode(true));
    setEditBookmarkName('');
    setEditBookmarkId(-1);
  };

  function renderBookmark(bookmark: Bookmark, index) {
    const { starred, name, id } = bookmark;
    const isEditing = editBookmarkId === id && inLocationEditMode;
    const isDeleting = deleteBookmarkId === id;
    let secondaryActions;
    let itemContent;

    if (!isEditing && !isDeleting) {
      // Normal bookmark view
      secondaryActions = (
        <>
          <IconButton
            className={styles.bookmark_edit_button}
            edge="end"
            aria-label="edit"
            title="Edit"
            onClick={(event) => onBookmarkEditClick(event, index)}
          >
            <Edit />
          </IconButton>
          <IconButton
            title="Star"
            onClick={(event) => onBookmarkStarClick(event, index)}
          >
            {(starred && <Star />) || <StarBorder />}
          </IconButton>
          <IconButton
            title="Delete"
            className={styles.bookmark_delete_button}
            onClick={(event) => {
              setDeleteBookmarkId(id);
              exitEditMode();
            }}
          >
            <Delete />
          </IconButton>
        </>
      );
      itemContent = (
        <ListItemButton
          className={styles.bookmark_item_button}
          onClick={(event) => onBookmarkSelect(event, index)}
        >
          <ListItemText primary={name} />
        </ListItemButton>
      );
    } else if (isDeleting) {
      secondaryActions = (
        <>
          <IconButton
            edge="end"
            aria-label="delete"
            title="Delete"
            color="error"
            onClick={(event) => onBookmarkDeleteConfirmClick(event, index)}
          >
            <Check />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="cancel"
            title="Cancel"
            color="success"
            onClick={(event) => setDeleteBookmarkId(undefined)}
          >
            <Close />
          </IconButton>
        </>
      );
      itemContent = (
        <ListItemText
          className={styles.confirm_delete_field}
          primary={`Delete '${name}'?`}
        />
      );
    } else {
      const editErrorString = updateNameError(
        name,
        editBookmarkName,
        bookmarks.map((e) => e.name),
        true,
      );
      // Editing view
      secondaryActions = (
        <>
          <IconButton
            edge="end"
            aria-label="submit edit"
            title="Submit edit"
            onClick={(event) => onBookmarkSubmitClick(event, index)}
            disabled={!!editErrorString}
          >
            <Check />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="cancel"
            title="Cancel"
            onClick={(event) => exitEditMode()}
          >
            <Close />
          </IconButton>
        </>
      );
      itemContent = (
        <TextField
          label="Name"
          value={editBookmarkName}
          onChange={(event) => setEditBookmarkName(event.target.value)}
          variant="standard"
          size="small"
          className={styles.edit_name_field}
          error={!!editErrorString}
          helperText={editErrorString}
        />
      );
    }

    return (
      <ListItem
        disablePadding
        secondaryAction={secondaryActions}
        selected={id === editBookmarkId}
        key={id}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (editBookmarkId) onBookmarkSubmitClick(e, index);
          }
        }}
      >
        {itemContent}
      </ListItem>
    );
  }

  return (
    <>
      <div className={styles.header_padded}>
        {!showSearch && (
          <>
            {showTitle && (
              <>
                <Typography variant="h2" fontWeight={400} alignSelf="center">
                  Locations
                </Typography>
                <InfoWalkmeCrumb elId="locations" />
              </>
            )}
            <IconButton
              onClick={onAddActivate}
              size="small"
              title="Add location"
            >
              <FontAwesomeIcon name="plus" sx={{ color: 'unset' }} />
            </IconButton>
            <IconButton
              onClick={(event) => setShowSearch(true)}
              size="small"
              title="Search locations"
            >
              <Search />
            </IconButton>
          </>
        )}
        {showSearch && (
          <>
            <TextField
              label="Location name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              variant="standard"
              size="small"
              className={styles.search_text_field}
            />
            <IconButton
              onClick={(event) => setShowSearch(false)}
              size="small"
              title="Hide search"
            >
              <Close />
            </IconButton>
          </>
        )}
      </div>
      {inLocationEditMode && editBookmarkId === -1 && (
        <div className={styles.header_padded}>
          <TextField
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddSubmit(e);
              }
            }}
            label="Type name here"
            value={editBookmarkName}
            onChange={(event) => setEditBookmarkName(event.target.value)}
            variant="standard"
            size="small"
            className={styles.search_text_field}
            error={
              !!updateNameError(
                undefined,
                editBookmarkName,
                bookmarks.map((e) => e.name),
              )
            }
            helperText={updateNameError(
              undefined,
              editBookmarkName,
              bookmarks.map((e) => e.name),
            )}
          />
          <IconButton
            onClick={onAddSubmit}
            disabled={
              !displayedBookmark ||
              !!updateNameError(
                undefined,
                editBookmarkName,
                bookmarks.map((e) => e.name),
                true,
              )
            }
            size="small"
          >
            <Check />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="cancel"
            title="Cancel"
            onClick={(event) => {
              dispatch(setInLocationEditMode(false));
            }}
          >
            <Close />
          </IconButton>
        </div>
      )}
      {bookmarkData?.bookmarks.length !== 0 ? (
        <List className={styles.bookmark_toolbar_list}>
          {bookmarks && bookmarks.map(renderBookmark)}
        </List>
      ) : (
        <div>
          <p className={styles.no_bookmarks_message}>No locations found</p>
          <p className={styles.no_bookmarks_message}>Press + to add</p>
        </div>
      )}
    </>
  );
}
